import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { isMobileApp } from 'utils/common';
import { closeMenu } from 'store/entities/dashboard/actions';
import { selectMenuOpened } from 'store/entities/dashboard/selectors';

/**
 * Safety net in case the drawer's exit transition never reports completion
 * (e.g. the webview suspends timers): navigate anyway after this delay.
 * The exit transition itself takes ~200ms.
 */
export const NAVIGATION_FALLBACK_DELAY = 500;

/**
 * Navigation from the menu drawer.
 *
 * On mobile the drawer is a temporary (modal) drawer. Navigating while it is
 * still animating out can mount the next screen (and potentially another
 * modal, e.g. the user-mapping dialog) mid-exit, which can swallow the
 * transition end and leave the drawer stuck in a collapsed state. To avoid
 * that, `navigateFromMenu` closes the drawer first and defers the navigation
 * until `onDrawerExited` (wired to the drawer transition's `onExited`) fires.
 */
const useMenuDrawerNavigation = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const mobileApp = isMobileApp();
  const menuOpened = useSelector(selectMenuOpened());

  const pendingLinkRef = useRef<string | null>(null);
  const fallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearFallbackTimer = useCallback(() => {
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
  }, []);

  const flushPendingNavigation = useCallback(() => {
    clearFallbackTimer();

    const link = pendingLinkRef.current;
    if (!link) {
      return;
    }

    pendingLinkRef.current = null;
    navigate(link);
  }, [clearFallbackTimer, navigate]);

  const navigateFromMenu = useCallback(
    (link: string) => {
      if (!mobileApp || !menuOpened) {
        navigate(link);
        return;
      }

      pendingLinkRef.current = link;
      dispatch(closeMenu());
      clearFallbackTimer();
      fallbackTimerRef.current = setTimeout(flushPendingNavigation, NAVIGATION_FALLBACK_DELAY);
    },
    [clearFallbackTimer, dispatch, flushPendingNavigation, menuOpened, mobileApp, navigate]
  );

  useEffect(() => clearFallbackTimer, [clearFallbackTimer]);

  return { navigateFromMenu, onDrawerExited: flushPendingNavigation };
};

export { useMenuDrawerNavigation };
